import { ICoordPair } from "@/modules/ToolModule/TwoPointsPicker";
import { LayerComponentBase } from "@/stellar-globe-components";
import { linspace } from "@/utils/math";
import { EquatorialCoord, Layer, path, View } from "@hscmap/stellar-globe";
import { Component, Prop, Watch } from "vue-property-decorator";

class RectOutlineLayer extends Layer {
  private pr!: path.Renderer

  constructor(private coordPair?: ICoordPair) {
    super()
  }

  onAdd() {
    this.pr = new path.Renderer(this.globe.gl)
    this.pr.darkenNarrowLine = false
    this.pr.blendMode = path.BlendMode.NORMAL
    this.setCoordPair(this.coordPair)
  }

  onRemove() {
    this.pr.release()
  }

  setCoordPair(coordPair?: ICoordPair) {
    this.coordPair = coordPair
    if (coordPair) {
      const p = this.pathFromCoordPair(coordPair)
      this.pr.setPaths([p])
    }
    this.globe.requestRefresh()
  }

  private pathFromCoordPair(coordPair: ICoordPair) {
    const { c0, c1 } = coordPair
    const da = Math.abs(c0.a.rad - c1.a.rad)
    const dd = Math.abs(c0.d.rad - c1.d.rad)
    const na = Math.floor(da / Math.PI * 180) + 1
    const nd = Math.floor(dd / Math.PI * 180) + 1
    const points = [
      ...linspace(c0.a.rad, c1.a.rad, na).map((a) => [a, c0.d.rad]),
      ...linspace(c0.d.rad, c1.d.rad, nd).map((d) => [c1.a.rad, d]),
      ...linspace(c1.a.rad, c0.a.rad, na).map((a) => [a, c1.d.rad]),
      ...linspace(c1.d.rad, c0.d.rad, nd).map((d) => [c0.a.rad, d]),
    ].map(([a, d]) => {
      return new path.Point(
        EquatorialCoord.fromRad(a, d).xyz,
        [1, 0, 1, 0.75],
        0,
      )
    })
    return new path.Path(points, true)
  }

  render(view: View) {
    if (this.coordPair) {
      this.pr.render(view)
    }
  }
}

@Component
export class RectOutline extends LayerComponentBase<RectOutlineLayer> {
  @Prop({ required: true })
  area?: ICoordPair
  @Watch('area')
  onAreaChange() {
    this.layer.setCoordPair(this.area)
  }

  newLayer() {
    return new RectOutlineLayer(this.area)
  }
}
